import dataService from './DataService';
import TimeStamp from '../utils/TimeStamp';
import { Opportunities, Quotes, QuoteLines } from '../utils/Database';

class QuoteService {
  constructor() {
    this.PREFIX = 'ACME';
    this.SUFFIX = 'A';
    this.QUOTE_NUMBER_SETTING = 'QuoteNumber';
    this.QUOTE_TYPE = 'QuoteSettings';
  }

  // Inverse une chaîne de caractères
  reverseString(str) {
    return str.split('').reverse().join('');
  }

  // Formate un nombre sur 4 chiffres
  padNumber(number) {
    return number.toString().padStart(4, '0');
  }

  // Récupère le numéro de devis actuel sans l'incrémenter
  async getCurrentQuoteNumber() {
    const types = await dataService.getTypes(this.QUOTE_TYPE);
    const quoteSetting = types.find(t => t.name === this.QUOTE_NUMBER_SETTING);

    if (!quoteSetting) {
      throw new Error('Quote number setting not found');
    }

    return {
      currentNumber: parseInt(quoteSetting.data) || 0,
      settingId: quoteSetting.id,
      setting: quoteSetting
    };
  }

  // Sauvegarde le nouveau numéro de devis
  async saveQuoteNumber(nextNumber, settingId, setting) {
    await dataService.update('settings', settingId, {
      ...setting,
      data: nextNumber.toString()
    });
  }

  // Génère le prochain numéro de devis sans le sauvegarder
  async generateNextQuoteNumber() {
    const { currentNumber } = await this.getCurrentQuoteNumber();
    return currentNumber + 1;
  }

  // Génère l'identifiant personnalisé complet sans sauvegarder le numéro
  async generateQuoteCustomId() {
    const currentYear = new Date().getFullYear();
    const reversedYear = this.reverseString(this.padNumber(currentYear));
    const nextNumber = await this.generateNextQuoteNumber();
    const reversedQuoteNumber = this.reverseString(this.padNumber(nextNumber));
    return `${this.PREFIX}${reversedYear}${reversedQuoteNumber}${this.SUFFIX}`;
  }

  // Sauvegarde le numéro de devis utilisé
  async commitQuoteNumber() {
    const { currentNumber, settingId, setting } = await this.getCurrentQuoteNumber();
    const nextNumber = currentNumber + 1;
    await this.saveQuoteNumber(nextNumber, settingId, setting);
    return nextNumber;
  }

  /**
   * Clone un devis existant vers un autre compte en créant une nouvelle opportunité.
   * @param {string} sourceQuoteCustomId - quoteCustomId du devis source
   * @param {number} targetAccountId - id du compte cible
   * @param {Object} options - { opportunityName, quoteName }
   * @returns {Promise<Object>} - le devis cloné (objet sauvegardé)
   */
  async cloneToAccount(sourceQuoteCustomId, targetAccountId, options = {}) {
    // Récupérer le devis source
    const sourceQuote = await dataService.getQuoteByCustomId(sourceQuoteCustomId);
    if (!sourceQuote) {
      throw new Error('Devis source introuvable');
    }

    // Récupérer la hiérarchie des lignes du devis source
    const quoteLines = await dataService.getQuoteLineHierarchy(sourceQuote.id);

    // Créer une opportunité sur le compte cible
    const baseName = sourceQuote.name || sourceQuoteCustomId;
    const opportunityName = options.opportunityName || `${baseName} - Copie`;
    const newOpportunity = new Opportunities(opportunityName, targetAccountId, TimeStamp.fromDate(new Date()));
    const savedOpportunity = await newOpportunity.save();

    // Générer un nouveau quoteCustomId (numéro consommé uniquement après succès)
    const newQuoteCustomId = await this.generateQuoteCustomId();
    const quoteName = options.quoteName || `${baseName} - Copie`;

    // Créer le nouveau devis (statut Draft)
    const newQuote = new Quotes(
      newQuoteCustomId,
      savedOpportunity.id,
      TimeStamp.fromDate(new Date()),
      'Draft',
      quoteName,
      true,
      sourceQuote.notes,
      sourceQuote.delay,
      sourceQuote.outOfScope,
      sourceQuote.needsFromClient
    );

    const savedNewQuote = await newQuote.save();

    // Map pour stocker les correspondances oldId -> newId
    const idMapping = new Map();

    // Fonction récursive pour cloner les lignes et leurs sous-lignes
    const cloneLineWithChildren = async (line, parentId = null) => {
      const newLine = new QuoteLines(
        line.description,
        savedNewQuote.id,
        parentId, // parent (nouveau id) ou null
        line.unitPrice,
        line.quantity,
        line.productId,
        line.position
      );

      const savedLine = await newLine.save();
      idMapping.set(line.id, savedLine.id);

      if (line.subLines && line.subLines.length > 0) {
        for (const subLine of line.subLines) {
          await cloneLineWithChildren(subLine, savedLine.id);
        }
      }
    };

    // Cloner les lignes de premier niveau
    for (const line of quoteLines) {
      if (!line.parentLineId) {
        await cloneLineWithChildren(line);
      }
    }

    // Marquer l'éventuel devis principal existant pour cette opportunité comme non principal
    const oldQuotes = await dataService.filter('quotes', {
      opportunityId: savedOpportunity.id,
      isPrimary: true
    });

    for (const oldQuote of oldQuotes) {
      if (oldQuote.id !== savedNewQuote.id) {
        await dataService.update('quotes', oldQuote.id, { isPrimary: false });
      }
    }

    // S'assurer que le nouveau devis est principal
    await dataService.update('quotes', savedNewQuote.id, { isPrimary: true });

    // Après succès complet, persister le numéro de devis consommé
    await this.commitQuoteNumber();

    return savedNewQuote;
  }

  // Extrait le suffixe (version) d'un quoteCustomId
  extractVersion(quoteCustomId) {
    return quoteCustomId.slice(-1);
  }

  // Incrémente une lettre (A -> B, B -> C, etc.)
  incrementLetter(letter) {
    if (letter === 'Z') {
      throw new Error('Version maximale atteinte (Z)');
    }
    return String.fromCharCode(letter.charCodeAt(0) + 1);
  }

  // Crée une nouvelle version d'un devis
  createNewVersion(quoteCustomId) {
    // Vérifie que le quoteCustomId est valide
    if (!quoteCustomId || quoteCustomId.length < 12) {
      throw new Error('QuoteCustomId invalide');
    }

    // Extrait la version actuelle
    const currentVersion = this.extractVersion(quoteCustomId);
    
    // Calcule la nouvelle version
    const newVersion = this.incrementLetter(currentVersion);
    
    // Remplace l'ancien suffixe par le nouveau
    return quoteCustomId.slice(0, -1) + newVersion;
  }
}

const quoteService = new QuoteService();
export default quoteService;
